import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { Button, Message } from "semantic-ui-react";

import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import { Discussion } from "components/lib/Discussion/Discussion";

import { useIdea } from "./hooks/useIdea";

type CommentsProps = { forId: string; isViewingMergedIdea?: boolean };

const IdeaComments = ({ forId, isViewingMergedIdea }: CommentsProps) => {
  const [idea] = useIdea();
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const onFollow = useCallback(
    (context: string, ideaId: string) =>
      dispatch(actions.user.onFollow(context, ideaId)),
    [dispatch],
  );

  const [commentLoading, setCommentLoading] = useState(false);
  const [replies, setReplies] = useState<{
    [key: string]: Partial<OpenAPI.Schemas["IdeaComment"]> | undefined;
  }>({});
  const [page, setPage] = useState(1);

  const [commentText, setCommentText] = useState("");
  const [commentMedia, setCommentMedia] = useState<
    OpenAPI.Schemas["IdeaComment"]["media"]
  >([]);

  const commentQuery = useQuery(
    initUseQuery(api.ideas.getComments, [forId, { page }], {
      isEnabled:
        !!forId && !!user && !idea?.ownerChallenge?.preventIdeaComments,
    }),
  );

  const onPageChange = (page: number) => setPage(page);

  const handlePost = (
    data: { message: string; media: OpenAPI.Schemas["IdeaComment"]["media"] },
    parentCommentId = "",
  ) => {
    setCommentLoading(true);

    const media = data.media?.map((item) => {
      const mediaItem = { ...item };
      if ("signedDownloadRequest" in mediaItem) {
        delete mediaItem.signedDownloadRequest;
      }
      return mediaItem;
    });

    api.ideas.postComment(
      forId,
      { ...data, media, message: data.message.replace("<?insert!>", "") },
      () => {
        toast.success(t("ideas.comments.posted"));
        api.invalidateQueries(api.ideas.get, [idea?._id]);

        onFollow("idea", forId);
        setCommentLoading(false);
        if (parentCommentId) {
          setReplies((prevState) => ({
            ...prevState,
            [parentCommentId]: undefined,
          }));
          api.invalidateQueries(api.ideas.getComments, [forId, { page }]);
        } else {
          setCommentText("");
          setCommentMedia([]);

          api.invalidateQueries(api.ideas.getComments, [forId, { page }]);
        }
      },
      (err) => {
        setCommentLoading(false);
        toast.error(err.message);
      },
    );
  };

  const post = () => {
    if (commentText || commentMedia) {
      const data = { message: commentText, media: commentMedia };
      handlePost(data);
    }
  };

  const postReply = (parentCommentId: string) => {
    const replyData = replies[parentCommentId];
    if (replyData?.comment || replyData?.media) {
      const data = {
        message: replyData.comment,
        media: replyData.media,
        parent: parentCommentId,
      };
      handlePost(data, parentCommentId);
    }
  };

  const deleteComment = (commentId: string) => {
    return util
      .confirm(
        t("ideas.comments.delete.title"),
        t("ideas.comments.delete.info"),
      )
      .then(() => {
        api.ideas.deleteComment(
          forId,
          commentId,
          () => {
            api.setQueryData(
              api.ideas.getComments,
              [forId, { page }],
              (prev) => ({
                ...prev,
                comments: prev.comments.filter((c) => c._id !== commentId),
              }),
            );
            api.invalidateQueries(api.ideas.get, [idea?._id]);
            toast(t("ideas.comments.delete.success"));
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  };

  const handleAddAttachment = (
    attachment: NonNullable<OpenAPI.Schemas["IdeaComment"]["media"]>[number],
    parentCommentId = "",
  ) => {
    if (parentCommentId) {
      setReplies((prevState) => ({
        ...prevState,
        [parentCommentId]: {
          ...prevState[parentCommentId],
          media: [...(prevState[parentCommentId]?.media || []), attachment],
        },
      }));
    } else {
      setCommentMedia((prev) => [...(prev || []), attachment]);
    }
  };

  const addAttachment = (
    storedName: string,
    name: string,
    signedDownloadRequest: string,
    parentId?: string,
  ) => {
    const attachment = {
      value: storedName,
      name,
      signedDownloadRequest,
      type:
        name.match(/\.(jpg)|(png)|(gif)|(jpeg)$/i) ?
          ("image" as const)
        : ("file" as const),
    };
    handleAddAttachment(attachment, parentId);
  };

  const addGiphy = (
    gif: { url: string; title: string },
    parentCommentId = "",
  ) => {
    const attachment = {
      value: gif.url,
      name: gif.title,
      type: "gif" as const,
      data: gif,
    };
    handleAddAttachment(attachment, parentCommentId);
  };

  const deleteAttachment = (index: number, parentCommentId = "") => {
    if (parentCommentId) {
      setReplies((prev) => ({
        ...prev,
        [parentCommentId]: {
          ...replies[parentCommentId],
          media: replies[parentCommentId]?.media?.filter((_, i) => i !== index),
        },
      }));
    } else {
      setCommentMedia((prev) => prev?.filter((_, i) => i !== index));
    }
  };

  const getFile = (att: OpenAPI.Schemas["IdeaComment"]["media"][number]) =>
    att.signedDownloadRequest || att.value;

  const updateComment = (commentText: string) => {
    setCommentText(commentText);
  };

  const updateReply = (parentCommentId: string, replyText: string) => {
    setReplies((prevState) => ({
      ...prevState,
      [parentCommentId]: {
        ...(replies[parentCommentId] || {}),
        comment: replyText,
      },
    }));
  };

  const pin = (commentId: string) => {
    api.ideas.pinComment(
      idea?._id,
      commentId,
      true,
      () => {
        api.invalidateQueries(api.ideas.getComments, [forId]);
      },
      (err) => toast.error(err.message),
    );
  };

  const unpin = (commentId: string) => {
    api.ideas.pinComment(
      idea?._id,
      commentId,
      false,
      () => {
        api.invalidateQueries(api.ideas.getComments, [forId]);
      },
      (err) => toast.error(err.message),
    );
  };

  const reactComment = (commentId: string, emoji: string, adding: boolean) => {
    const func =
      adding ? api.ideas.commentAddReaction : api.ideas.commentRemoveReaction;
    func(
      idea?._id,
      commentId,
      emoji,
      (data) => {
        api.setQueryData(api.ideas.getComments, [forId, { page }], (prev) => ({
          ...prev,
          comments: prev.comments.map((c) =>
            c._id === commentId ?
              {
                ...c,
                reactCount: data?.reacts?.length || 0,
                reacts: data.reacts,
              }
            : c,
          ),
        }));
      },
      () => {},
    );
  };

  const userId = user?._id;
  const isAssessor = idea?.assessments?.find((a) => a.user === userId);

  return (
    <div data-testid="idea-comments">
      {isAssessor && (
        <Message warning size="tiny">
          {`These comments are visible to anyone with access to this ${t("generic.idea")}. This includes the authors, ${t("generic.challenge")} managers, any assessors, and anyone else who has the link.`}
        </Message>
      )}
      {user ?
        <Discussion
          getReplyData={(parentCommentId) =>
            replies[parentCommentId] ?
              {
                text: replies[parentCommentId].comment || "",
                media: replies[parentCommentId].media || [],
              }
            : { text: "", media: [] }
          }
          canManage={
            util.canManageChallenge(user, idea?.ownerChallenge) ||
            util.hasPermission(
              user,
              "challenge.manageIdeaComments",
              idea?.challenge,
            )
          }
          post={post}
          deleteComment={deleteComment}
          feed={commentQuery.data?.comments || []}
          loading={commentQuery.isLoading}
          type={"idea"}
          updateComment={updateComment}
          commentLoading={commentLoading}
          commentText={commentText}
          commentMedia={commentMedia}
          getFile={getFile}
          fileChooserForType="idea"
          fileChooserForId={forId}
          addGiphy={addGiphy}
          addAttachment={addAttachment}
          deleteAttachment={deleteAttachment}
          canPost={true}
          pin={pin}
          unpin={unpin}
          postReply={postReply}
          updateReply={updateReply}
          reactComment={reactComment}
          page={page}
          nextPageAvailable={commentQuery.data?.nextPageAvailable || false}
          previousPageAvailable={
            commentQuery.data?.previousPageAvailable || false
          }
          total={commentQuery.data?.total || 0}
          limit={999}
          onPageChange={onPageChange}
          message={
            idea?.parent && isViewingMergedIdea ?
              `You are commenting on the ${t("generic.idea")} "${idea?.name}"`
            : undefined
          }
        />
      : <Message>
          <h4>Please log in to view comments</h4>
          <p>
            Register or login to your account to view and interact with comments
            on this {t("generic.idea")}.
          </p>
          <Button primary as={Link} to={`/login?then=/ideas/${idea?._id}`}>
            Login or register
          </Button>
        </Message>
      }
    </div>
  );
};

export default IdeaComments;
